import { useState } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar as CalendarIcon, Clock, MapPin, User, Phone, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { PayPalPaymentButton } from './PayPalPaymentButton';

// Time slots available (working 8 AM - 6 PM)
const allTimeSlots = [
  '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', 
  '04:00 PM', '05:00 PM'
];

// Service types with duration and buffer times (in hours)
const serviceTypes = [
  { 
    name: 'Quick Light Clean (1 hour)', 
    duration: 1, 
    bufferBefore: 0.5,  // 30 min before
    bufferAfter: 0.5,   // 30 min after
    type: 'standard'
  },
  { 
    name: 'Kitchen Cleaning', 
    duration: 2, 
    bufferBefore: 0.5,  // 30 min before
    bufferAfter: 0.5,   // 30 min after
    type: 'standard'
  },
  { 
    name: 'Bathroom & Toilet Cleaning', 
    duration: 2, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  },
  { 
    name: 'Laundry Room Cleaning', 
    duration: 2, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  },
  { 
    name: 'Vacate Clean (Bond Cleaning)', 
    duration: 6, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  },
  { 
    name: 'Multiple Areas (2-3 areas)', 
    duration: 3, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  },
  { 
    name: 'Airbnb & Short Stay Cleaning', 
    duration: 3,        // 3 hours cleaning
    bufferBefore: 0,    // No buffer before
    bufferAfter: 1,     // 60 min after
    type: 'airbnb'
  },
  { 
    name: 'Full House Clean', 
    duration: 3, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  },
  { 
    name: 'Full Wet Area Package', 
    duration: 4, 
    bufferBefore: 0.5,
    bufferAfter: 0.5,
    type: 'standard'
  }
];

// Calculate total booking time including buffers
const calculateTotalBlockedTime = (serviceType) => {
  const service = serviceTypes.find(s => s.name === serviceType);
  if (!service) return 2;
  return service.bufferBefore + service.duration + service.bufferAfter;
};

// Calculate actual start time (including buffer before)
const calculateActualStartTime = (timeSlot, serviceType) => {
  const service = serviceTypes.find(s => s.name === serviceType);
  if (!service || service.bufferBefore === 0) return timeSlot;
  
  // Convert time to minutes and subtract buffer
  const [hours, minutes] = timeSlot.split(':').map(Number);
  const totalMinutes = (hours * 60 + minutes) - (service.bufferBefore * 60);
  const newHours = Math.floor(totalMinutes / 60);
  const newMinutes = totalMinutes % 60;
  
  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
};

// Calculate end time based on start time and duration
const calculateEndTime = (startTime, durationHours) => {
  const timeMap = {
    '08:00 AM': 8, '09:00 AM': 9, '10:00 AM': 10, '11:00 AM': 11,
    '12:00 PM': 12, '01:00 PM': 13, '02:00 PM': 14, '03:00 PM': 15,
    '04:00 PM': 16, '05:00 PM': 17
  };
  
  const startHour = timeMap[startTime];
  const endHour = startHour + durationHours;
  
  if (endHour <= 12) {
    return `${endHour === 12 ? 12 : endHour}:00 ${endHour < 12 ? 'AM' : 'PM'}`;
  } else {
    const hour12 = endHour > 12 ? endHour - 12 : endHour;
    return `${hour12 === 0 ? 12 : hour12.toString().padStart(2, '0')}:00 PM`;
  }
};

// Filter available time slots based on service duration and buffers
const getAvailableTimeSlots = (serviceType) => {
  const service = serviceTypes.find(s => s.name === serviceType);
  if (!service) return allTimeSlots;
  
  const totalTime = service.bufferBefore + service.duration + service.bufferAfter;
  
  return allTimeSlots.filter(slot => {
    const timeMap = {
      '08:00 AM': 8, '09:00 AM': 9, '10:00 AM': 10, '11:00 AM': 11,
      '12:00 PM': 12, '01:00 PM': 13, '02:00 PM': 14, '03:00 PM': 15,
      '04:00 PM': 16, '05:00 PM': 17
    };
    const startHour = timeMap[slot];
    const startWithBuffer = startHour - service.bufferBefore;
    const endWithBuffer = startHour + service.duration + service.bufferAfter;
    
    // Make sure the service (including all buffers) fits within working hours
    return startWithBuffer >= 8 && endWithBuffer <= 18;
  });
};

export const BookingForm = () => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    suburb: '',
    postcode: '',
    serviceType: '',
    timeSlot: '',
    additionalNotes: ''
  });
  
  const [selectedServiceDuration, setSelectedServiceDuration] = useState(2);
  const [availableSlots, setAvailableSlots] = useState(allTimeSlots);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleServiceTypeChange = (value) => {
    const selectedService = serviceTypes.find(s => s.name === value);
    if (selectedService) {
      setSelectedServiceDuration(selectedService.duration);
      setAvailableSlots(getAvailableTimeSlots(value));
      handleInputChange('serviceType', value);
      // Reset time slot when service changes
      handleInputChange('timeSlot', '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!date) {
      toast.error('Please select a date for your appointment');
      return;
    }
    
    if (!formData.name || !formData.phone || !formData.address || !formData.serviceType || !formData.timeSlot) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prepare booking data
    const selectedService = serviceTypes.find(s => s.name === formData.serviceType);
    const preparedBookingData = {
      name: formData.name,
      email: formData.email || null,
      phone: formData.phone,
      address: formData.address,
      suburb: formData.suburb || null,
      postcode: formData.postcode || null,
      service_type: formData.serviceType,
      date: format(date, 'yyyy-MM-dd'),
      time_slot: formData.timeSlot,
      duration: selectedServiceDuration,
      buffer_before: selectedService?.bufferBefore || 0.5,
      buffer_after: selectedService?.bufferAfter || 0.5,
      additional_notes: formData.additionalNotes || null
    };

    // Store booking data and show payment
    setBookingData(preparedBookingData);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (booking) => {
    const endTime = calculateEndTime(formData.timeSlot, selectedServiceDuration);
    
    toast.success('Booking confirmed! Payment received.', {
      description: `${format(date, 'EEEE, MMMM d, yyyy')} • ${formData.timeSlot} - ${endTime}`
    });

    // Reset form
    setDate(undefined);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      suburb: '',
      postcode: '',
      serviceType: '',
      timeSlot: '',
      additionalNotes: ''
    });
    setSelectedServiceDuration(2);
    setAvailableSlots(allTimeSlots);
    setShowPayment(false);
    setBookingData(null);
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed', {
      description: 'Please try again or call us directly'
    });
  };

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Appointment
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select your preferred date and time, and we'll take care of the rest
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-gray-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50 border-b">
              <CardTitle className="text-2xl">Schedule Your Cleaning Service</CardTitle>
              <CardDescription className="text-base">
                Fill in your details and select your preferred appointment time
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Calendar Section */}
                <div>
                  <Label className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <CalendarIcon size={20} className="text-teal-600" />
                    Select Date
                  </Label>
                  <div className="flex justify-center bg-white border-2 border-gray-200 rounded-lg p-4">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date() || unavailableDates.some(d => 
                        new Date(d).toDateString() === date.toDateString()
                      )}
                      className="rounded-md"
                    />
                  </div>
                  {date && (
                    <p className="mt-3 text-center text-teal-600 font-semibold">
                      Selected: {format(date, 'EEEE, MMMM d, yyyy')}
                    </p>
                  )}
                </div>

                {/* Service Type Selection */}
                <div>
                  <Label htmlFor="serviceType" className="text-lg font-semibold mb-3 block">
                    Service Type *
                  </Label>
                  <Select value={formData.serviceType} onValueChange={handleServiceTypeChange}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((service) => (
                        <SelectItem key={service.name} value={service.name}>
                          <div className="flex items-center justify-between w-full">
                            <span>{service.name}</span>
                            <span className="ml-4 text-xs text-gray-500">
                              ({service.duration} {service.duration === 1 ? 'hour' : 'hours'})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.serviceType && (
                    <p className="mt-2 text-sm text-teal-600 font-medium">
                      Duration: {selectedServiceDuration} {selectedServiceDuration === 1 ? 'hour' : 'hours'} • 2 cleaners
                    </p>
                  )}
                </div>

                {/* Time Slot Selection */}
                <div>
                  <Label htmlFor="timeSlot" className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Clock size={20} className="text-teal-600" />
                    Preferred Start Time *
                  </Label>
                  <Select 
                    value={formData.timeSlot} 
                    onValueChange={(value) => handleInputChange('timeSlot', value)}
                    disabled={!formData.serviceType}
                  >
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder={formData.serviceType ? "Select start time" : "Select service type first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.map((slot) => {
                        const endTime = calculateEndTime(slot, selectedServiceDuration);
                        return (
                          <SelectItem key={slot} value={slot}>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{slot}</span>
                              <span className="text-xs text-gray-500">→ {endTime}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {formData.timeSlot && formData.serviceType && (
                    <p className="mt-2 text-sm text-gray-600">
                      Service time: {formData.timeSlot} - {calculateEndTime(formData.timeSlot, selectedServiceDuration)}
                    </p>
                  )}
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User size={20} className="text-teal-600" />
                    Contact Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="mb-2 block">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="John Smith"
                        className="h-12"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="mb-2 block">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+61 4XX XXX XXX"
                        className="h-12"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label htmlFor="email" className="mb-2 block">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="john@example.com"
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-teal-600" />
                    Service Address
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address" className="mb-2 block">Street Address *</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="123 Main Street"
                        className="h-12"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="suburb" className="mb-2 block">Suburb</Label>
                        <Input
                          id="suburb"
                          value={formData.suburb}
                          onChange={(e) => handleInputChange('suburb', e.target.value)}
                          placeholder="Sydney"
                          className="h-12"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="postcode" className="mb-2 block">Postcode</Label>
                        <Input
                          id="postcode"
                          value={formData.postcode}
                          onChange={(e) => handleInputChange('postcode', e.target.value)}
                          placeholder="2000"
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="additionalNotes" className="mb-2 block">Additional Notes</Label>
                  <Textarea
                    id="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                    placeholder="Any specific requirements or areas that need special attention?"
                    className="min-h-24 resize-none"
                  />
                </div>

                {!showPayment ? (
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg font-semibold bg-teal-600 hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Continue to Payment
                  </Button>
                ) : (
                  <div>
                    <Button 
                      type="button"
                      onClick={() => setShowPayment(false)}
                      variant="outline"
                      className="w-full mb-4"
                    >
                      ← Back to Edit Details
                    </Button>
                    <PayPalPaymentButton
                      bookingData={bookingData}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
